import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import {
	Dispatch,
	FC,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import {
	ActionIcon,
	Button,
	Flex,
	Group,
	NumberInput,
	Popover,
	ScrollArea,
	Select,
	Text,
	TextInput,
	Tooltip,
	Tree,
} from '@mantine/core';
import { DateValue } from '@mantine/dates';
import { isNotEmpty, useForm } from '@mantine/form';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useGetPassTypesQuery } from '@/api/pass-types.ts';

import { AppContext } from '@/contexts/AppContext/AppContextProvider.tsx';

import { UserSearch } from '@/entities/User';

import { bookingObjectsApi } from '@/shared/api/bookingObjects.ts';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction.tsx';
import { IRequestError } from '@/shared/types/app.types.ts';
import { IObject, IObjectRequest } from '@/shared/types/bookingObjects.types.ts';
import { LabelInput, SwitchSelector } from '@/shared/uikit/components/input';
import classes from '@/shared/uikit/components/input/LabelInput/LabelInput.module.css';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { DateField } from '@/shared/uikit/components/picker/DateField/DateField';

import { IconCalendarStats, IconChevronDown } from '@tabler/icons-react';

type Props = {
	isOpen: boolean;
	object: IObject | null | undefined;
	objects: IObject[] | undefined;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	handleClose: () => void;
};

interface TreeDataType {
	label: string;
	value: string;
	children: TreeDataType[] | undefined;
}

interface IObjectForm {
	parent_id: number;
	name: string;
	description: string;
	type_id: number | undefined;
	svg: string;
	owner_person_id: number | undefined;
	owner_change_date?: DateValue | null;
	booking_pass_type_id: number | undefined;
	moderator_pass_type_id: number | undefined;
	for_moderator_only: boolean;
	is_blocked: boolean;
	order_column: number | string;
}

export const ObjectForm: FC<Props> = ({ isOpen, object, objects, setIsOpen, handleClose }) => {
	const queryClient = useQueryClient();
	const { admin, moderator } = useContext(AppContext);
	const [inputState, setInputState] = useState('');
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);

	const { data: objectTypes } = useQuery({
		queryKey: ['objectTypes'],
		queryFn: bookingObjectsApi.getObjectsTypes,
	});
	const { data: objectActions, isLoading } = useQuery({
		queryKey: ['objectActions', object?.id],
		queryFn: () => bookingObjectsApi.getObjectsActions(object?.id),
		enabled: !!object,
	});
	const { data: passTypes } = useGetPassTypesQuery('passtypes', { skip: !admin });

	const { mutate: createObject, isPending: isCreating } = useMutation({
		mutationFn: bookingObjectsApi.createObject,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['objects'] });
			useShowNotifaction({ text: 'Объект создано', type: 'success' });
			setIsOpen(false);
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: error.response?.data?.message ?? '',
				type: 'error',
			});
		},
	});

	const { mutate: updateObject, isPending: isUpdating } = useMutation({
		mutationFn: bookingObjectsApi.updateObject,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['objects'] });
			useShowNotifaction({ text: 'Объект изменен', type: 'success' });
			setIsOpen(false);
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: error.response?.data?.message ?? '',
				type: 'error',
			});
		},
	});

	const form = useForm<IObjectForm>({
		initialValues: {
			parent_id: object?.parent_id ?? 0,
			name: object?.name ?? '',
			description: object?.description ?? '',
			type_id: object?.type_id ?? undefined,
			svg: object?.svg ?? '',
			owner_person_id: object?.owner_person_id ?? undefined,
			owner_change_date: object?.owner_change_date ?? null,
			booking_pass_type_id: object?.booking_pass_type_id ?? undefined,
			moderator_pass_type_id: object?.moderator_pass_type_id ?? undefined,
			for_moderator_only: object?.for_moderator_only ?? false,
			is_blocked: object?.is_blocked ?? false,
			order_column: object?.order_column ?? '',
		},
		validate: {
			name: isNotEmpty(),
			description: isNotEmpty(),
			type_id: isNotEmpty(),
		},
	});

	const filteredObjectActions = objectActions?.objects.filter((action) => !action.is_deleted);

	const buildTree = useCallback(
		(id?: number): TreeDataType[] => {
			if (objects) {
				return objects
					.filter((item) => item.parent_id === id)
					.map((item) => {
						const { name, id } = item;

						return {
							label: name,
							value: `${item.id}`,
							children: buildTree(id)?.length ? buildTree(id) : undefined,
						};
					});
			}
			return [];
		},
		[objects],
	);

	const treeData = useMemo(() => buildTree(), [buildTree]);

	const handleSubmit = (payload: IObjectRequest) => {
		if (object) {
			updateObject({ payload, id: object.id });
			return;
		}
		createObject(payload);
	};

	useEffect(() => {
		if (object) {
			form.setValues({
				parent_id: object?.parent_id ?? 0,
				name: object?.name,
				description: object?.description,
				type_id: object?.type_id,
				svg: object?.svg ?? undefined,
				owner_person_id: object?.owner_person_id ?? undefined,
				owner_change_date: object?.owner_change_date ?? null,
				booking_pass_type_id: object?.booking_pass_type_id ?? undefined,
				moderator_pass_type_id: object?.moderator_pass_type_id ?? undefined,
				for_moderator_only: object?.for_moderator_only ?? false,
				is_blocked: object?.is_blocked ?? false,
				order_column: object?.order_column ?? '',
			});

			setInputState(objects?.find((elem) => elem.id === object?.parent_id)?.name ?? '');
		}
	}, [object]);

	return (
		<ModalSheet
			title={`${object ? 'Изменение' : 'Создание'} узла`}
			opened={isOpen}
			onClose={() => {
				handleClose();
				form.reset();
			}}
			size="lg"
			scrollAreaComponent={ScrollArea.Autosize}
		>
			<form
				onSubmit={form.onSubmit((values) => {
					if (!values.type_id) return;

					const requestValues: Omit<IObjectForm, 'order_column'> & {
						order_column: number | null;
					} = {
						...values,
						type_id: values.type_id,
						order_column: typeof values.order_column === 'number' ? values.order_column : null,
					};

					if (values.owner_change_date) {
						requestValues.owner_change_date = dayjs(values.owner_change_date).utc(true).toDate();
					} else {
						delete requestValues.owner_change_date;
					}

					handleSubmit(requestValues as unknown as IObjectRequest);
					// form.reset();
				})}
			>
				<Flex direction="column" gap="sm">
					<Popover
						width="target"
						trapFocus
						position="bottom"
						shadow="md"
						opened={isPopoverOpen}
						onChange={setIsPopoverOpen}
					>
						<Popover.Target>
							<TextInput
								classNames={classes}
								onClick={() => setIsPopoverOpen(true)}
								label={
									<Text c="#667085" fz={12} fw={500} lh="normal" tt="uppercase">
										Родительский узел
									</Text>
								}
								placeholder="Выберите родительский узел"
								onKeyDown={(event) => event.preventDefault()}
								defaultValue={inputState}
								// rightSection={
								// 	<IconX
								// 		size={20}
								// 		onClick={() => {
								// 			setInputState('');
								// 			form.setFieldValue('parent_id', 0);
								// 		}}
								// 		cursor="pointer"
								// 	/>
								// }
								disabled={!admin}
							/>
						</Popover.Target>
						<Popover.Dropdown>
							<ScrollArea.Autosize mah={300} scrollbarSize={6} offsetScrollbars>
								<Tree
									data={treeData}
									renderNode={({ node, expanded, hasChildren, elementProps }) => (
										<Flex justify="space-between" align="center" {...elementProps}>
											{hasChildren && (
												<IconChevronDown
													onClick={(event) => {
														event.preventDefault();
													}}
													size={14}
													style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
												/>
											)}
											<Text
												onClick={(event) => {
													event.stopPropagation();

													setInputState(`${String(node.label)}`);
													form.setFieldValue('parent_id', Number(node.value));

													setIsPopoverOpen(false);
												}}
												// fz="lg"
												w="100%"
												{...(!hasChildren ? { ml: 'md' } : {})}
											>
												{node.label}
											</Text>
										</Flex>
									)}
								/>
							</ScrollArea.Autosize>
						</Popover.Dropdown>
					</Popover>
					<Select
						classNames={classes}
						label={
							<Text c="#667085" fz={12} fw={500} lh="normal" tt="uppercase">
								Тип узла
							</Text>
						}
						data={
							objectTypes?.objects
								.filter((item) => !item.is_deleted)
								.map((elem) => {
									return { value: `${elem.id}`, label: elem.name };
								}) ?? [{ value: '', label: '' }]
						}
						placeholder="Выберите тип узла"
						allowDeselect={false}
						disabled={!admin}
						value={`${form.getInputProps('type_id').value}`}
						onChange={(value) => form.getInputProps('type_id').onChange(Number(value))}
						error={form.getInputProps('type_id').error}
						withAsterisk
					/>
					<LabelInput
						classNames={classes}
						label="Имя"
						placeholder="Введите имя узла"
						disabled={!admin}
						{...form.getInputProps('name')}
						withAsterisk
					/>
					<LabelInput
						classNames={classes}
						label="Описание"
						placeholder="Введите описание узла"
						disabled={!admin}
						{...form.getInputProps('description')}
						withAsterisk
					/>
					<NumberInput
						classNames={classes}
						label={
							<Text c="#667085" fz={12} fw={500} lh="normal" tt="uppercase">
								Порядок сортировки
							</Text>
						}
						description="Меньшее значение — выше среди объектов того же родителя. Без значения — после пронумерованных, по алфавиту. При первоначальной нумерации используйте шаг 10 (10, 20, 30…), чтобы потом вставлять объекты между соседями без перенумерации."
						inputWrapperOrder={['label', 'input', 'description', 'error']}
						placeholder="Не задан"
						allowDecimal={false}
						allowNegative={false}
						min={0}
						max={16777216}
						{...form.getInputProps('order_column')}
					/>

					<UserSearch
						label="Владелец"
						initialValue={form.getInputProps('owner_person_id').value}
						setPersonId={(personId) => form.setFieldValue('owner_person_id', personId)}
						onClear={() => form.setFieldValue('owner_person_id', undefined)}
					/>

					{form.getValues().owner_person_id !== undefined && (
						<Flex gap={10} align={'center'}>
							<DateField
								{...form.getInputProps('owner_change_date')}
								label={'Будущая дата (опционально)'}
								placeholder={'Дата'}
								clearable
							/>
							{!!filteredObjectActions?.length && (
								<Popover position="bottom" withArrow shadow="md">
									<Popover.Target>
										<Tooltip label="Запланированные даты">
											<ActionIcon variant="filled" size={'lg'} loading={isLoading}>
												<IconCalendarStats style={{ width: '80%', height: '80%' }} stroke={1} />
											</ActionIcon>
										</Tooltip>
									</Popover.Target>
									<Popover.Dropdown>
										{filteredObjectActions?.map((action) => {
											return <Text>{dayjs(action.execution_date).format('DD.MM.YYYY')}</Text>;
										})}
									</Popover.Dropdown>
								</Popover>
							)}
						</Flex>
					)}

					{admin && (
						<>
							<Select
								classNames={classes}
								label={
									<Text c="#667085" fz={12} fw={500} lh="normal" tt="uppercase">
										Тип пропуска
									</Text>
								}
								data={
									passTypes?.objects.map((elem) => {
										return { value: `${elem.id}`, label: elem.name };
									}) ?? [{ value: '', label: '' }]
								}
								placeholder="Выберите тип"
								allowDeselect={false}
								value={`${form.getInputProps('booking_pass_type_id').value}`}
								onChange={(value) =>
									form.getInputProps('booking_pass_type_id').onChange(Number(value))
								}
							/>
							<Select
								classNames={classes}
								label={
									<Text c="#667085" fz={12} fw={500} lh="normal" tt="uppercase">
										Тип пропуска для модератора
									</Text>
								}
								data={
									passTypes?.objects.map((elem) => {
										return { value: `${elem.id}`, label: elem.name };
									}) ?? [{ value: '', label: '' }]
								}
								placeholder="Выберите тип"
								allowDeselect={false}
								value={`${form.getInputProps('moderator_pass_type_id').value}`}
								onChange={(value) =>
									form.getInputProps('moderator_pass_type_id').onChange(Number(value))
								}
							/>
							<LabelInput
								classNames={classes}
								label="SVG-path"
								placeholder="m -77.352058,1686.8409 z"
								{...form.getInputProps('svg')}
							/>
						</>
					)}

					<SwitchSelector
						name="Заблокировано"
						checked={form.getInputProps('is_blocked').value}
						onChange={(value) => form.getInputProps('is_blocked').onChange(value)}
					/>
					{admin && (
						<SwitchSelector
							name="Только для модератора"
							checked={form.getInputProps('for_moderator_only').value}
							onChange={(value) => form.getInputProps('for_moderator_only').onChange(value)}
						/>
					)}
					<Group gap="sm" mt="lg">
						<Button type="submit" size="md" loading={isCreating || isUpdating}>
							Сохранить
						</Button>
						<Button
							variant="outline"
							size="md"
							onClick={() => {
								handleClose();
								form.reset();
							}}
						>
							Отмена
						</Button>
					</Group>
				</Flex>
			</form>
		</ModalSheet>
	);
};
